import { ChangeRequest, ChangeRequestStatus } from '@app/core/model/change-request.model'
import { ScopeVersion } from '@app/core/model/scope-version'
import { Action, createReducer, on } from '@ngrx/store'
import { changeRequestInitialState } from '../../const/change-request-initial-state.const'
import { ChangeRequestState } from '../../model/change-request.state.model'
import { ChangeRequestActions } from '../actions/change-request.actions'
import { ChangeItemAdapter } from '../adapters/change-item.adapter'
import { ChangeItem, ChangeItemType, ChangeType } from '@app/features/scope-overview/change-request/model'
import { plainToInstance } from 'class-transformer'
import { keyBy } from 'lodash'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'

const createChangeRequestSuccessHandler = (state: ChangeRequestState, { changeRequest }) => {
  const currentChangeRequest = plainToInstance(ChangeRequest, changeRequest)

  return {
    ...state,
    changeRequests: [...state.changeRequests, currentChangeRequest],
    changeItems: ChangeItemAdapter.getInitialState(),
    currentChangeRequest: currentChangeRequest,
  }
}

const updateCurrentChangeRequestStatusHandler = (
  state: ChangeRequestState,
  { status }: { status: ChangeRequestStatus }
): ChangeRequestState => {
  if (!state.currentChangeRequest) {
    return state
  }

  const updatedChangeRequest = plainToInstance(ChangeRequest, {
    ...state.currentChangeRequest,
    status,
  })

  return {
    ...state,
    currentChangeRequest: updatedChangeRequest,
  }
}

const changeRequestAbleToMergeSuccessHandler = (state: ChangeRequestState, action: { ableToMerge: boolean }) => ({
  ...state,
  ableToMerge: action.ableToMerge,
})

const resetChangeRequest = (state: ChangeRequestState) => ({
  ...state,
  currentChangeRequest: null,
  changeItems: ChangeItemAdapter.getInitialState(),
})

const getChangeRequestHandler = (state: ChangeRequestState) => ({
  ...state,
  loading: { ...state.loading, changeRequest: true },
})

const getChangeRequestSuccessHandler = (state: ChangeRequestState, action: { changeRequest: ChangeRequest }) => {
  const changeRequest = plainToInstance(ChangeRequest, action.changeRequest)
  const changeItems: ChangeItem[] = changeRequest.changes?.map(mapChangeItem) || []

  return {
    ...state,
    currentChangeRequest: changeRequest,
    changeItems: ChangeItemAdapter.setAll(changeItems, state.changeItems || ChangeItemAdapter.getInitialState()),
    loading: { ...state.loading, changeRequest: false },
  }
}

const getChangeRequestFailHandler = (state: ChangeRequestState) => ({
  ...state,
  currentChangeRequest: undefined,
  changeItems: ChangeItemAdapter.getInitialState(),
  loading: { ...state.loading, changeRequest: false },
})

const getChangeRequestsSuccessHandler = (state: ChangeRequestState, action: { changeRequests: ChangeRequest[] }) => ({
  ...state,
  changeRequests: action.changeRequests,
})

const getChangeRequestsFailHandler = (state: ChangeRequestState) => ({
  ...state,
  changeRequests: [],
})

const setCurrentChangeRequestHandler = (state: ChangeRequestState, action: { changeRequest: ChangeRequest | null }) => {
  if (action.changeRequest) {
    const changeRequest = plainToInstance(ChangeRequest, action.changeRequest)
    const changeItems: ChangeItem[] = changeRequest.changes?.map(mapChangeItem) || []

    return {
      ...state,
      currentChangeRequest: changeRequest,
      changeItems: ChangeItemAdapter.setAll(changeItems, state.changeItems || ChangeItemAdapter.getInitialState()),
    }
  }

  return {
    ...state,
    currentChangeRequest: undefined,
    changeItems: ChangeItemAdapter.getInitialState(),
  }
}

const applyChangesSuccessHandler = (state: ChangeRequestState, { changeItem }) => {
  const mappedChangeItem = mapChangeItem(changeItem)
  const updatedChanges = [...(state.currentChangeRequest?.changes || []), mappedChangeItem]
  const updatedChangeRequest = state.currentChangeRequest
    ? plainToInstance(ChangeRequest, { ...state.currentChangeRequest, changes: updatedChanges })
    : undefined

  return {
    ...state,
    currentChangeRequest: updatedChangeRequest,
    changeItems: ChangeItemAdapter.upsertOne(changeItem, state.changeItems),
  }
}

const getChangeRequestPreviewHandler = (state: ChangeRequestState) => ({
  ...state,
  loading: { ...state.loading, previewScope: true },
})

const getChangeRequestPreviewSuccessHandler = (state: ChangeRequestState, action: { previewScope: ScopeVersion }) => {
  const previewScope = plainToInstance(ScopeVersion, action.previewScope)

  return {
    ...state,
    previewScope,
    previewDeliverablesMap: keyBy(previewScope?.deliverables, 'id'),
    loading: { ...state.loading, previewScope: false },
  }
}

const getChangeRequestPreviewFailHandler = (state: ChangeRequestState) => ({
  ...state,
  previewScope: undefined,
  previewDeliverablesMap: {},
  loading: { ...state.loading, previewScope: false },
})

const generateChangesFromScenarioConfigSuccessHandler = (
  state: ChangeRequestState,
  action: { changeRequest: ChangeRequest }
) => {
  const changeRequest = plainToInstance(ChangeRequest, action.changeRequest)
  const changeItems: ChangeItem[] = changeRequest.changes?.map(mapChangeItem) || []

  return {
    ...state,
    currentChangeRequest: changeRequest,
    changeItems: ChangeItemAdapter.setAll(changeItems, state.changeItems || ChangeItemAdapter.getInitialState()),
  }
}

const removeChangesSuccessHandler = (state: ChangeRequestState, { changeItemUuid }: { changeItemUuid: string }) => {
  if (!state.changeItems) return state

  // Find the change item by UUID to get its identity (which is used as the adapter ID)
  const changeItemToRemove = state.changeItems.entities
    ? Object.values(state.changeItems.entities).find((item) => item?.uuid === changeItemUuid)
    : null

  if (!changeItemToRemove || !changeItemToRemove.identity) return state

  const updatedChangeItems = ChangeItemAdapter.removeOne(changeItemToRemove.identity, state.changeItems)
  const updatedChanges = state.currentChangeRequest?.changes?.filter((item) => item.uuid !== changeItemUuid) || []
  const updatedChangeRequest = state.currentChangeRequest
    ? plainToInstance(ChangeRequest, { ...state.currentChangeRequest, changes: updatedChanges })
    : undefined

  return {
    ...state,
    currentChangeRequest: updatedChangeRequest,
    changeItems: updatedChangeItems,
  }
}

function mapChangeItem(changeItem: ChangeItem) {
  const item = { ...changeItem }
  if (item.type === ChangeItemType.DELIVERABLE) {
    const changes = item.changeType === ChangeType.ADDED ? item.data?.data : item.data
    item.data = changes
  }
  return plainToInstance(ChangeItem, item)
}

const setMappedDeliverablesHandler = (state: ChangeRequestState, action: { deliverables: Deliverable[] }) => ({
  ...state,
  mappedDeliverables: action.deliverables,
})
const setMappedTrackedDeliverablesHandler = (state: ChangeRequestState, action: { deliverables: Deliverable[] }) => ({
  ...state,
  mappedTrackedDeliverables: action.deliverables,
})

const changeRequestReducerInternal = createReducer(
  changeRequestInitialState,
  on(ChangeRequestActions.updateCurrentChangeRequestStatus, updateCurrentChangeRequestStatusHandler),
  on(ChangeRequestActions.createChangeRequestSuccess, createChangeRequestSuccessHandler),
  on(ChangeRequestActions.resetCurrentChangeRequest, resetChangeRequest),
  on(ChangeRequestActions.changeRequestAbleToMergeSuccess, changeRequestAbleToMergeSuccessHandler),
  on(ChangeRequestActions.getChangeRequest, getChangeRequestHandler),
  on(ChangeRequestActions.getChangeRequestSuccess, getChangeRequestSuccessHandler),
  on(ChangeRequestActions.getChangeRequestFail, getChangeRequestFailHandler),
  on(ChangeRequestActions.getChangeRequestsSuccess, getChangeRequestsSuccessHandler),
  on(ChangeRequestActions.getChangeRequestsFail, getChangeRequestsFailHandler),
  on(ChangeRequestActions.setCurrentChangeRequest, setCurrentChangeRequestHandler),
  on(ChangeRequestActions.applyChangesSuccess, applyChangesSuccessHandler),
  on(ChangeRequestActions.removeChangesSuccess, removeChangesSuccessHandler),
  on(ChangeRequestActions.generateChangesFromScenarioConfigSuccess, generateChangesFromScenarioConfigSuccessHandler),
  on(ChangeRequestActions.getChangeRequestPreview, getChangeRequestPreviewHandler),
  on(ChangeRequestActions.getChangeRequestPreviewSuccess, getChangeRequestPreviewSuccessHandler),
  on(ChangeRequestActions.getChangeRequestPreviewFail, getChangeRequestPreviewFailHandler),
  on(ChangeRequestActions.setMappedDeliverables, setMappedDeliverablesHandler),
  on(ChangeRequestActions.setMappedTrackedDeliverables, setMappedTrackedDeliverablesHandler)
)

export function changeRequestReducer(state: ChangeRequestState | undefined, action: Action): ChangeRequestState {
  return changeRequestReducerInternal(state, action)
}
